import React, { useEffect, useState } from 'react';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import './ReportsFilterComponent';
import { useApi } from '../../api/ApiProvider';
import Api from '../../axiosApi/api';
import { ReportsFilterComponentProps } from './ReportsFilterComponent';
import { TimeSheetDetailedReport } from '../../axiosApi/models';
import { getUserEntity} from 'common/UserEntityProvider';
import { PermissionsGate } from '../authorization/PermissionsGate';
import intl from 'components/i18n/ReactIntlWrapper';
import { ENTITY_TYPE } from 'common/constants';
import { convertDateAsUTC2 } from 'common/utils';
import { handleAPIError } from '../../common/errorHandler';
import { Chart, useChart } from 'components/chart';
import { Box, Card, CardContent, CardHeader, Skeleton, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

interface Task {
    idTask: number;
    taskDescription: string;
    time: number;
    idEmployee: number;
    employeeName: string;
    date: Date
}

interface Job {
    idJob: number;
    jobDescription: string;
    tasks: Task[];
    billableHours: number;
    totalHours: number;
    nonBillableHours: number;
}

interface Client {
    idClient: number;
    clientName: string;
    projects: Project[];
    totalHours: number;
    billableHours: number;
    nonBillableHours: number;
}

interface Project {
    idProject: number;
    projectName: string;
    jobs: Job[];
    totalHours: number;
    billableHours: number;
    nonBillableHours: number;
}

interface DateEntry {
    date: string;
    billableHours: number;
    nonBillableHours: number;
}

interface Employee {
    idEmployee: number;
    employeeName: string;
    totalHours: number;
    billableHours: number;
    nonBillableHours: number;
    monthlyRate: number;
    hourlyRate: number;
    billingAmount: number;
}

export const MyTimesheetStatusComponent = ({ errors, setErrors}) => {

    const api: Api = useApi();
    const toast = useToastMessageQueue();
    const theme = useTheme();
    const [chartData, setChartData] = React.useState<any>(null);
    const userEntity = getUserEntity();
    const idEmployee = userEntity?.entityType === ENTITY_TYPE.EMPLOYEE ? userEntity?.entityId : null;
    const currentDate = new Date(); // Get the current date
    const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const lastDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    const [averageBillable, setAverageBillable] = useState(0);
    const [averageTotal, setAverageTotal] = useState(0);
    const [loadingState, setLoadingState] = useState(false);
    
    // Format the month and year
    const monthNames = [
        "January", "February", "March", "April", "May", "June", 
        "July", "August", "September", "October", "November", "December"
    ];
    const monthName = monthNames[firstDayOfMonth.getMonth()];
    const year = firstDayOfMonth.getFullYear();
    const monthYearDisplay = `${monthName} ${year}`;

    useEffect(() => {
        if(!!idEmployee)
            {
            fetchData({
                from: convertDateAsUTC2(firstDayOfMonth),
                to: convertDateAsUTC2(lastDayOfMonth),
                projects: "",
                clients: "",
                employees: idEmployee.toString(),
                jobTypes: ""
            });
        }
    }, [idEmployee]);

    const fetchData = async (filterData: ReportsFilterComponentProps) => {
        setLoadingState(true);
        await api.reportsApi.apiVversionTimeSheetDetailedReportAllGet("1", filterData.from, filterData.to, filterData.projects, filterData.clients, filterData.employees, filterData.jobTypes, {}).then(async (response) => {
            if (response.data.data) {
                createClientProjectJobTaskTree(response.data?.data);
            };
        }).catch((error) => {
            handleAPIError(error, toast, errors);
            setErrors({...errors});
        }).finally(() => {
            setLoadingState(false);
        });
    };

    function createClientProjectJobTaskTree(timesheetData: TimeSheetDetailedReport[]) {
        const clients: Client[] = [];
        const dates: DateEntry[] = [];
        const employees: Employee[] = [];

        for (const entry of timesheetData) {
            let client = clients.find((c) => c.idClient === entry.idClient);

            if (!client) {
                client = {
                    idClient: entry.idClient,
                    clientName: entry.clientName,
                    projects: [],
                    billableHours: entry.billable ? entry.time : 0,
                    nonBillableHours: entry.billable ? 0 : entry.time,
                    totalHours: entry.time
                };
                clients.push(client);
            }
            else {
                client.billableHours += entry.billable ? entry.time : 0;
                client.nonBillableHours += entry.billable ? 0 : entry.time;
                client.totalHours += entry.time;
            }


            let project = client.projects.find((p) => p.idProject === entry.idProject);

            if (!project) {
                project = {
                    idProject: entry.idProject,
                    projectName: entry.projectName,
                    jobs: [],
                    billableHours: entry.billable ? entry.time : 0,
                    nonBillableHours: entry.billable ? 0 : entry.time,
                    totalHours: entry.time

                };
                client.projects.push(project);
            }
            else {
                project.billableHours += entry.billable ? entry.time : 0;
                project.nonBillableHours += entry.billable ? 0 : entry.time;
                project.totalHours += entry.time;
            }

            let job = project.jobs.find((j) => j.idJob === entry.idJob);

            if (!job) {
                job = {
                    idJob: entry.idJob,
                    jobDescription: entry.jobDescription,
                    tasks: [],
                    billableHours: entry.billable ? entry.time : 0,
                    nonBillableHours: !entry.billable ? entry.time : 0,
                    totalHours: entry.time
                };
                project.jobs.push(job);
            }
            else {
                job.billableHours += entry.billable ? entry.time : 0;
                job.nonBillableHours += entry.billable ? 0 : entry.time;
                job.totalHours += entry.time;
            }


            const task: Task = {
                idTask: entry.idTimesheet,
                taskDescription: entry.taskDescription,
                time: entry.time,
                idEmployee: entry.idEmployee,
                employeeName: entry.employeeName,
                date: entry.date
            };
            job.tasks.push(task);

            let date = dates.find((d) => d.date === new Date(entry.date).toLocaleDateString());

            if (!date) {
                date = {
                    date: new Date(entry.date).toLocaleDateString(),
                    billableHours: entry.billable ? entry.time : 0,
                    nonBillableHours: !entry.billable ? entry.time : 0
                };
                dates.push(date);
            }
            else {
                date.billableHours += entry.billable ? entry.time : 0;
                date.nonBillableHours += entry.billable ? 0 : entry.time;
            }

            let employee = employees.find((j) => j.idEmployee === entry.idEmployee);
            if (!employee) {
                employee = {
                    idEmployee: entry.idEmployee,
                    employeeName: entry.employeeName,
                    billableHours: entry.billable ? entry.time : 0,
                    nonBillableHours: entry.billable ? 0 : entry.time,
                    totalHours: entry.time,
                    hourlyRate: entry.hourlyRate,
                    monthlyRate: entry.monthlyRate,
                    billingAmount: !entry.billable ? 0 : (entry.hourlyRate > 0 ? entry.time / 60 * entry.hourlyRate : 0)
                }
                employees.push(employee);
            }
            else {
                employee.billableHours += entry.billable ? entry.time : 0;
                employee.nonBillableHours += entry.billable ? 0 : entry.time;
                employee.totalHours += entry.time;
                employee.billingAmount += !entry.billable ? 0 : (entry.hourlyRate > 0 ? (entry.time * entry.hourlyRate) / 60 : 0);
            }

        }

        const billable = dates.length>0?dates.map(x=>x.billableHours):[];
        const nonBillable = dates.length>0?dates.map(x=>x.nonBillableHours + x.billableHours):[];
        const avgBillable = dates.length>0?(billable.reduce((a,c)=> a+c)/dates.length)/60:0;
        const avgTotal = dates.length>0?(nonBillable.reduce((a,c)=> a+c)/dates.length)/60:0;
        
        setAverageBillable(avgBillable);
        setAverageTotal(avgTotal);

        // Create array with all dates in the month
        const allDates: DateEntry[] = [];
        let currentDate = new Date(firstDayOfMonth);
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Reset time portion for accurate comparison

        while (currentDate <= lastDayOfMonth) {
            const dateStr = currentDate.toLocaleDateString();
            
            // Find existing date entry or create a new one
            const existingDate = dates.find(d => d.date === dateStr);
            if (existingDate) {
                allDates.push(existingDate);
            } else {
                allDates.push({
                    date: dateStr,
                    billableHours: 0,
                    nonBillableHours: 0
                });
            }
            
            // Move to next day
            currentDate = new Date(currentDate);
            currentDate.setDate(currentDate.getDate() + 1);
        }

        // Sort dates chronologically
        const sortedDates = allDates.sort((a, b) => {
            return new Date(a.date).getTime() - new Date(b.date).getTime();
        });

        // Determine which dates are weekends, today, or future
        const dateInfo = sortedDates?.map(dateEntry => {
            const date = new Date(dateEntry.date);
            date.setHours(0, 0, 0, 0); // Reset time portion for accurate comparison
            const day = date.getDay();
            const isWeekend = day === 0 || day === 6;
            const isToday = date.getTime() === today.getTime();
            const isFuture = date > today;
            
            return {
                isWeekend,
                isToday,
                isFuture
            };
        });

        // Format dates more consistently
        const formattedDates = sortedDates?.map((entry, index) => {
            const date = new Date(entry.date);
            return {
                ...entry,
                formattedDate: `${date.getDate()}`
            };
        });

        // Convert data to ApexCharts format
        const chartDataFormatted = {
            categories: formattedDates?.map(c => c.formattedDate),
            series: [
                {
                    name: intl.get('jobProject.body.billable'),
                    data: formattedDates?.map((c) => parseFloat((c.billableHours / 60).toFixed(2)) || null),
                },
                {
                    name: intl.get('billingReportComponent.clientRow.tableCel.nonBillable'),
                    data: formattedDates?.map((c) => parseFloat((c.nonBillableHours / 60).toFixed(2)) || null),
                }
            ],
            dateInfo // Add date info for styling
        };

        setChartData(chartDataFormatted);
    }
   
    // Configure chart options using the useChart hook
    const chartOptions = useChart({
        stroke: {
            width: 2,
        },
        chart: {
            stacked: true,
        },
        plotOptions: {
            bar: {
                columnWidth: '80%',
                borderRadius: 1,
            },
        },
        grid: {
            show: true,
            xaxis: {
                lines: {
                    show: true,
                }
            },
            yaxis: {
                lines: {
                    show: true
                }
            },
            padding: {
                top: 0,
                right: 0,
                bottom: 0,
                left: 0
            },
        },
        yaxis: {
            min: 0,
            forceNiceScale: false,
            tickAmount: 12,
            labels: {
                formatter: (value: number) => `${value.toFixed(0)} h`,
                style: {
                    fontSize: '10px'
                }
            },
            title: {
                text: 'Hours',
            },
        },
        xaxis: {
            tickPlacement: 'on',
            labels: {
                formatter: function(value, timestamp, opts) {
                    return value; // Return the value unchanged
                },
                style: {
                    colors: chartData?.dateInfo?.map(info => {
                        if (info.isToday) return theme.palette.primary.main; // Today
                        if (info.isWeekend) return theme.palette.error.main; // Weekend
                        if (info.isFuture) return theme.palette.text.disabled; // Future dates
                        return theme.palette.text.secondary; // Regular past dates
                    }) || []
                }
            },
        },
        tooltip: {
            y: {
                formatter: (value: number) => `${value} hours`,
            }
        },
        legend: {
            show: true,
            position: 'top',
            horizontalAlign: 'right',
        },
        annotations: {
            yaxis: [
                {
                    y: averageBillable,
                    borderColor: theme.palette.success.main,
                    label: {
                        borderColor: theme.palette.success.main,
                        style: {
                            color: theme.palette.common.white,
                            background: theme.palette.success.main
                        },
                        text: `${intl.get('timesheetstatus.average')} ${intl.get('jobProject.body.billable')}: ${averageBillable.toFixed(2)}`,
                        position: 'left',
                        offsetX: 150,
                    }
                },
                {
                    y: averageTotal,
                    borderColor: theme.palette.secondary.main,
                    label: {
                        borderColor: theme.palette.secondary.main,
                        style: {
                            color: theme.palette.common.white,
                            background: theme.palette.secondary.main
                        },
                        text: `${intl.get('timesheetstatus.average')} Total: ${averageTotal.toFixed(2)}`,
                        position: 'right',
                        offsetX: -50,
                    }
                }
            ]
        }
    });
   
    return (
        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardHeader title={
                <Typography color="primary" variant="h6" component="h6">
                  {intl.get('myTimesheetStatusComponent.title')}
                </Typography>
              }
              subheader={monthYearDisplay}
            />
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative', pt: 0 }}>
                {loadingState ?
                        <Skeleton variant="rounded" width="100%" height="100%" />
                    :
                    <PermissionsGate identifier="ui.reports.my-timesheet.gate" entityType={ENTITY_TYPE.EMPLOYEE} viewRoles={[]} editRoles={[]} >
                        {chartData && (
                            <Chart
                                type="bar"
                                series={chartData.series}
                                options={useChart({
                                    ...chartOptions,
                                    colors: [theme.palette.info.main, theme.palette.error.main],
                                    xaxis: { 
                                        categories: chartData.categories,
                                        labels: {
                                            style: {
                                                colors: chartData.dateInfo?.map(info => {
                                                    if (info.isToday) return theme.palette.primary.main; // Today
                                                    if (info.isWeekend && info.isFuture) return theme.palette.error.light; // Future weekend
                                                    if (info.isFuture) return theme.palette.text.disabled; // Future dates
                                                    if (info.isWeekend) return theme.palette.error.main; // Past weekend
                                                    return theme.palette.text.secondary; // Regular past dates
                                                })
                                            },
                                            formatter: function(value, timestamp, opts) {
                                                return value;
                                            }
                                        }
                                    },
                                    dataLabels: {
                                        style: {
                                            colors: [theme.palette.common.white]
                                        }
                                    }
                                })}
                                height={"100%"}
                                sx={{ flex: 1 }}
                            />
                        )}
                    </PermissionsGate> 
                }
            </CardContent>
        </Card>
    )
}

